/**
 * Stage engagement band from PR #146 / StageChrome CENTER:
 * my 2vh + pt 7vh = 9vh, through 100vh - my 2vh - pb 3vh = 95vh.
 * Keep in sync with STAGE_CENTER_* ratios in slideLayout.ts.
 */
export const STAGE_ENGAGEMENT_AREA_TOP_RATIO = 0.09;
export const STAGE_ENGAGEMENT_AREA_BOTTOM_RATIO = 0.95;

export const DESIRED_WINNER_SCALE = 1.25;
export const MIN_WINNER_SCALE = 1;
export const WINNER_TITLE_ROTATION_DEG = -3;
/** Short-label size (`text-[1.5vw]`). Winners use this as full size. */
export const WINNER_TITLE_VW = 0.015;
/** Floor vs the winner’s full zoomed size (short-winner px at current zoom). */
export const MIN_RENDERED_TEXT_RATIO = 0.9;
/** @deprecated alias — 90% applies after zoom, not to the CSS em scale. */
export const MIN_BUBBLE_TEXT_SCALE = MIN_RENDERED_TEXT_RATIO;
/** Winners wrap to at most this many lines; never clamp below it for space. */
export const MAX_WINNER_BUBBLE_LINES = 3;
export const MAX_WINNER_BUBBLE_WIDTH_RATIO = 1.6;
export const BUBBLE_NEIGHBOR_GAP_PX = 8;
export const LOSER_COLUMN_SCALE = 0.95;

export type FitRect = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export type BubbleTextFit = {
  textScale: number;
  maxLines: number | null;
};

export function stageEngagementAreaRect(
  viewportWidth: number,
  viewportHeight: number,
): FitRect {
  return {
    top: viewportHeight * STAGE_ENGAGEMENT_AREA_TOP_RATIO,
    right: viewportWidth,
    bottom: viewportHeight * STAGE_ENGAGEMENT_AREA_BOTTOM_RATIO,
    left: 0,
  };
}

export function unionRects(
  rects: Array<FitRect | null | undefined>,
): FitRect | null {
  const present = rects.filter((rect): rect is FitRect => Boolean(rect));
  if (present.length === 0) {
    return null;
  }
  return present.reduce((acc, rect) => ({
    top: Math.min(acc.top, rect.top),
    right: Math.max(acc.right, rect.right),
    bottom: Math.max(acc.bottom, rect.bottom),
    left: Math.min(acc.left, rect.left),
  }));
}

export function zoomedY({
  unscaledY,
  originY,
  zoom,
}: {
  unscaledY: number;
  originY: number;
  zoom: number;
}): number {
  return originY + (unscaledY - originY) * zoom;
}

export function rotationHeightPad(width: number): number {
  return (
    Math.max(0, width) *
    Math.abs(Math.sin((WINNER_TITLE_ROTATION_DEG * Math.PI) / 180))
  );
}

export function winnerCssFontPx(viewportWidth: number): number {
  return viewportWidth * WINNER_TITLE_VW;
}

export function zoomedFullWinnerFontPx({
  viewportWidth,
  columnZoom,
}: {
  viewportWidth: number;
  columnZoom: number;
}): number {
  return winnerCssFontPx(viewportWidth) * columnZoom;
}

/** Unscaled stack between 9vh and the avatar after zoom. */
export function minBubbleStackPx({
  starOverhang,
  lineHeight,
  lines = MAX_WINNER_BUBBLE_LINES,
  textScale = 1,
}: {
  starOverhang: number;
  lineHeight: number;
  lines?: number;
  textScale?: number;
}): number {
  return (
    Math.max(0, starOverhang) +
    Math.max(lineHeight * textScale, 1) * Math.max(1, lines)
  );
}

/**
 * Column zoom from the vote-count baseline. Grows upward. Never below 1.
 * Caps so the zoomed avatar still leaves `minStackPx` (unscaled; scales with
 * zoom) under the 9vh line in screen space.
 */
export function computeWinnerColumnZoom({
  unscaledAvatarTop,
  originY,
  areaTop,
  minStackPx,
  desiredScale = DESIRED_WINNER_SCALE,
  minScale = MIN_WINNER_SCALE,
}: {
  unscaledAvatarTop: number;
  originY: number;
  areaTop: number;
  minStackPx: number;
  desiredScale?: number;
  minScale?: number;
}): number {
  // originY + (avatarTop - originY) * z >= areaTop + minStackPx * z
  const denom = minStackPx - unscaledAvatarTop + originY;
  let fit = Number.POSITIVE_INFINITY;
  if (denom > 1) {
    const zoom = (originY - areaTop) / denom;
    if (Number.isFinite(zoom) && zoom > 0) {
      fit = zoom;
    }
  }
  let scale = Math.min(desiredScale, fit);
  if (!Number.isFinite(scale) || scale < minScale) {
    scale = minScale;
  }
  return Math.round(Math.min(desiredScale, scale) * 1000) / 1000;
}

export function availableBubbleHeight({
  zoomedAvatarTop,
  areaTop,
  zoomedStarOverhang,
}: {
  zoomedAvatarTop: number;
  areaTop: number;
  zoomedStarOverhang: number;
}): number {
  return Math.max(
    0,
    zoomedAvatarTop - areaTop - Math.max(0, zoomedStarOverhang),
  );
}

/**
 * Widest centered bubble that stays ≤ 1.6× avatar and clear of neighbors.
 */
export function computeMaxCenteredBubbleWidth({
  avatarWidth,
  avatarCenter,
  leftObstacle,
  rightObstacle,
  extraLeft = 0,
  gap = BUBBLE_NEIGHBOR_GAP_PX,
  maxRatio = MAX_WINNER_BUBBLE_WIDTH_RATIO,
  zoom = 1,
}: {
  avatarWidth: number;
  avatarCenter: number;
  leftObstacle: number;
  rightObstacle: number;
  extraLeft?: number;
  gap?: number;
  maxRatio?: number;
  zoom?: number;
}): number {
  const z = Math.max(zoom, 1e-6);
  const leftRoomScreen = avatarCenter - extraLeft * z - leftObstacle - gap;
  const rightRoomScreen = rightObstacle - avatarCenter - gap;
  const byNeighbors =
    (2 * Math.min(Math.max(0, leftRoomScreen), Math.max(0, rightRoomScreen))) /
    z;
  const cap = Math.min(avatarWidth * maxRatio, byNeighbors);
  const width = Math.max(avatarWidth, Math.min(avatarWidth * maxRatio, cap));
  return Math.round(width * 1000) / 1000;
}

/**
 * Lowest CSS em scale so the rendered size after zoom is:
 * - at least 90% of the short-winner full zoomed size, and
 * - never smaller than the losing options’ labels (this rule always wins).
 */
export function minWinnerTextScale({
  cssFontPx,
  zoomedFullFontPx,
  loserRenderedFontPx,
  columnZoom,
  minRenderedRatio = MIN_RENDERED_TEXT_RATIO,
}: {
  cssFontPx: number;
  zoomedFullFontPx: number;
  loserRenderedFontPx: number;
  columnZoom: number;
  minRenderedRatio?: number;
}): number {
  const currentZoomed = cssFontPx * columnZoom;
  if (currentZoomed <= 0) return 1;
  const minRendered = Math.max(
    zoomedFullFontPx * minRenderedRatio,
    Math.max(0, loserRenderedFontPx),
  );
  return Math.round((minRendered / currentZoomed) * 1000) / 1000;
}

export function renderedFontAfterZoom({
  cssFontPx,
  textScale,
  columnZoom,
}: {
  cssFontPx: number;
  textScale: number;
  columnZoom: number;
}): number {
  return cssFontPx * textScale * columnZoom;
}

/**
 * Fit the title bubble into the gap between the engagement top (plus star)
 * and the avatar top. Prefer a full-size wrap of at most 3 lines. The 90%
 * floor is vs short-winner zoomed px; loser labels always win. Never clamp
 * below 3 lines for lack of space.
 */
export function computeBubbleTextFit({
  availableHeight,
  naturalHeight,
  lineHeight,
  minTextScale = MIN_RENDERED_TEXT_RATIO,
  maxLines = MAX_WINNER_BUBBLE_LINES,
}: {
  availableHeight: number;
  naturalHeight: number;
  lineHeight: number;
  minTextScale?: number;
  maxLines?: number;
}): BubbleTextFit {
  const safeLine = Math.max(lineHeight, 1);
  const lineCap = Math.max(1, Math.floor(maxLines));
  const threeLineHeight = Math.min(naturalHeight, safeLine * lineCap);
  const needsLineCap = naturalHeight > safeLine * lineCap + 0.5;
  const floor = minTextScale;

  if (availableHeight <= 0) {
    return { textScale: floor, maxLines: lineCap };
  }
  if (threeLineHeight * Math.max(1, floor) <= availableHeight + 0.5) {
    return {
      textScale: Math.max(1, floor),
      maxLines: needsLineCap ? lineCap : null,
    };
  }
  const scaleNeeded = availableHeight / threeLineHeight;
  if (scaleNeeded >= floor) {
    return {
      textScale: Math.round(scaleNeeded * 1000) / 1000,
      maxLines: needsLineCap ? lineCap : null,
    };
  }
  return { textScale: floor, maxLines: lineCap };
}

/**
 * Lower the shared vote-bar range so the winner’s voteHeight leaves room for
 * a full-size (optionally zoomed) name stack under 9vh. Other options keep
 * the same proportions against this new maximum.
 */
export function computeMaxVoteBarRange({
  originY,
  areaTop,
  columnBodyPx,
  reservedStackPx,
  winnerVotes,
  maxVotes,
  zoom = DESIRED_WINNER_SCALE,
  naiveMax,
}: {
  originY: number;
  areaTop: number;
  columnBodyPx: number;
  reservedStackPx: number;
  winnerVotes: number;
  maxVotes: number;
  zoom?: number;
  naiveMax: number;
}): number {
  const room =
    (originY - areaTop) / Math.max(zoom, 1e-6) - columnBodyPx - reservedStackPx;
  const share = Math.max(winnerVotes, 1e-6) / Math.max(maxVotes, 1e-6);
  const cap = room / share;
  if (!Number.isFinite(cap)) return Math.max(0, naiveMax);
  return Math.max(0, Math.min(naiveMax, cap));
}

export function winnerChoiceTransform(scale: number): string {
  return `scale(${scale})`;
}

export function winnerTitleTransform(): string {
  return `rotate(${WINNER_TITLE_ROTATION_DEG}deg)`;
}
